/**
 * Epic 9, E9-US5 — čisté zvýraznění SQL syntaxe pro náhled change scriptu.
 * Místo Monaca (uživatelské rozhodnutí — žádná nová těžká závislost s vlastní
 * worker konfigurací, aplikace jinak žádný editor komponentu nepoužívá) jen
 * malý regexový tokenizer nad `<pre>` blokem — postačí pro čtení/kontrolu,
 * kopírování a stažení, ne pro editaci (skript se stejně nikdy needituje
 * v UI, jen prohlíží).
 */
import type { ReactNode } from 'react';

const KEYWORDS = new Set([
  'ADD',
  'ALTER',
  'AS',
  'BEGIN',
  'CASCADE',
  'CHECK',
  'COLUMN',
  'COMMENT',
  'COMMIT',
  'CONSTRAINT',
  'CREATE',
  'DATA',
  'DEFAULT',
  'DELETE',
  'DROP',
  'FOREIGN',
  'FROM',
  'INDEX',
  'INSERT',
  'INTO',
  'IS',
  'KEY',
  'NO',
  'NOT',
  'NULL',
  'ON',
  'PRIMARY',
  'REFERENCES',
  'RENAME',
  'RESTRICT',
  'SELECT',
  'SET',
  'TABLE',
  'TO',
  'TYPE',
  'UNIQUE',
  'UPDATE',
  'USING',
  'VALUES',
  'WHERE',
]);

const TOKEN_PATTERN = /(--[^\n]*)|('(?:[^']|'')*')|("(?:[^"]|"")*")|(\b\d+(?:\.\d+)?\b)|(\b[A-Za-z_][A-Za-z0-9_]*\b)/g;

function tokenize(sql: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  TOKEN_PATTERN.lastIndex = 0;
  let match = TOKEN_PATTERN.exec(sql);
  while (match) {
    if (match.index > lastIndex) nodes.push(sql.slice(lastIndex, match.index));
    const [, comment, stringLiteral, quotedIdent, number, word] = match;
    if (comment) {
      nodes.push(
        <span key={key++} className="sql-token sql-token--comment">
          {comment}
        </span>,
      );
    } else if (stringLiteral) {
      nodes.push(
        <span key={key++} className="sql-token sql-token--string">
          {stringLiteral}
        </span>,
      );
    } else if (quotedIdent) {
      nodes.push(
        <span key={key++} className="sql-token sql-token--identifier">
          {quotedIdent}
        </span>,
      );
    } else if (number) {
      nodes.push(
        <span key={key++} className="sql-token sql-token--number">
          {number}
        </span>,
      );
    } else if (word) {
      if (KEYWORDS.has(word.toUpperCase())) {
        nodes.push(
          <span key={key++} className="sql-token sql-token--keyword">
            {word}
          </span>,
        );
      } else {
        nodes.push(word);
      }
    }
    lastIndex = TOKEN_PATTERN.lastIndex;
    match = TOKEN_PATTERN.exec(sql);
  }
  if (lastIndex < sql.length) nodes.push(sql.slice(lastIndex));
  return nodes;
}

interface SqlPreviewProps {
  sql: string;
}

export function SqlPreview({ sql }: SqlPreviewProps) {
  return (
    <pre className="sql-preview">
      <code>{tokenize(sql)}</code>
    </pre>
  );
}
